import axios from 'axios'

// FCM 토큰 등록/업데이트 요청 타입
export interface RegisterFcmTokenRequest {
  userId: number
  fcmToken: string | null // FCM 토큰이 없어도 기기정보는 저장 가능
  deviceInfo: {
    platform: 'ios' | 'android' | string
    model: string
    appVersion: string
  }
  notificationSettings?: {
    common?: boolean
    marketing?: boolean
    consultation?: boolean
  }
}

// FCM 토큰 등록/업데이트 응답 타입
export interface RegisterFcmTokenResponse {
  code: string
  message: string
  data: {
    success: boolean
    tokenId?: string
  }
}

// 사용자 알림 조회 응답 타입
export interface UserNotification {
  id: string
  title: string
  message: string
  data?: Record<string, any>
  imageUrl?: string
  notificationType: 'common' | 'marketing' | 'consultation'
  timestamp: string
  isRead: boolean
  createdAt: string
}

export interface GetUserNotificationsResponse {
  code: string
  message: string
  data: {
    notifications: UserNotification[]
    unreadCount: number
    hasMoreNotifications: boolean
  }
}

// 알림 읽음 처리 요청 타입
export interface MarkNotificationAsReadRequest {
  userId: number
  notificationIds?: string[] // 특정 알림들
  markAllAsRead?: boolean // 전체 읽음 처리
}

// 알림 읽음 처리 응답 타입
export interface MarkNotificationAsReadResponse {
  code: string
  message: string
  data: {
    success: boolean
    updatedCount: number
  }
}

// 알림 설정 업데이트 요청 타입
export interface UpdateNotificationSettingsRequest {
  userId: number
  fcmToken: string
  settings: {
    common: boolean
    marketing: boolean
    consultation: boolean
  }
}

// 알림 설정 업데이트 응답 타입
export interface UpdateNotificationSettingsResponse {
  code: string
  message: string
  data: {
    success: boolean
    settings: {
      common: boolean
      marketing: boolean
      consultation: boolean
    }
  }
}

// 알림 설정 조회 응답 타입
export interface GetNotificationSettingsResponse {
  code: string
  message: string
  data: {
    settings: {
      common: boolean
      marketing: boolean
      consultation: boolean
    }
  }
}

/**
 * FCM 토큰을 백엔드에 등록/업데이트합니다.
 * 실제로는 Firebase Functions의 manageFcmToken 엔드포인트를 호출합니다.
 */
export const registerFcmTokenApi = async (request: RegisterFcmTokenRequest): Promise<RegisterFcmTokenResponse> => {
  // Firebase Functions URL 직접 호출 (프록시 우회)
  const response = await axios.post('https://managefcmtoken-txjo4nocdq-du.a.run.app', request, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  return response.data
}

/**
 * 사용자의 알림 목록을 조회합니다.
 */
export const getUserNotificationsApi = async (userId: number): Promise<GetUserNotificationsResponse> => {
  // Firebase Functions URL 직접 호출
  const response = await axios.get(`https://getusernotifications-txjo4nocdq-du.a.run.app?userId=${userId}`)
  return response.data
}

/**
 * 알림을 읽음으로 처리합니다.
 */
export const markNotificationAsReadApi = async (
  request: MarkNotificationAsReadRequest
): Promise<MarkNotificationAsReadResponse> => {
  // Firebase Functions URL 직접 호출
  const response = await axios.post('https://marknotificationasread-txjo4nocdq-du.a.run.app', request, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  return response.data
}

/**
 * 사용자의 알림 설정을 업데이트합니다.
 */
export const updateNotificationSettingsApi = async (
  request: UpdateNotificationSettingsRequest
): Promise<UpdateNotificationSettingsResponse> => {
  // Firebase Functions URL 직접 호출
  const response = await axios.post('https://updatenotificationsettings-txjo4nocdq-du.a.run.app', request, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  return response.data
}

/**
 * 사용자의 알림 설정을 조회합니다.
 */
export const getNotificationSettingsApi = async (
  userId: number,
  fcmToken: string
): Promise<GetNotificationSettingsResponse> => {
  // Firebase Functions URL 직접 호출
  const response = await axios.get(
    `https://getnotificationsettings-txjo4nocdq-du.a.run.app?userId=${userId}&fcmToken=${fcmToken}`
  )
  return response.data
}
